import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import ProjectReviews from "@/components/projects/ProjectReviews";
import formatTime from "@/utils/formatTime";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Icon from "@/components/ui/Icon";
import Avatar from "@/components/ui/Avatar";
import { ButtonAnchor } from "@/components/ui/Button";
import { usePage } from "@inertiajs/react";
import type { HackatimeProject } from "@/interfaces/hackatime_project";
import type { Project, ProjectChange } from "@/interfaces/project";
import type { ProjectReview } from "@/interfaces/project_review";
import type { PublicUser, ReviewerUser } from "@/interfaces/user";
import type { SharedProps } from "@/types";
import clockIcon from "@/assets/icons/clock.svg";

interface Props {
  project: Project & {
    user: ReviewerUser;
    reviews: (ProjectReview & { author: PublicUser })[];
  };
  ships: ProjectChange[];
  hackatime_projects: HackatimeProject[];
  shipped_for: { id: number; name: string } | null;
  attributable_events: { id: number; name: string }[];
}

export default function ReviewShow({
  project,
  ships,
  hackatime_projects,
  shipped_for,
  attributable_events,
}: Props) {
  const { program } = usePage<SharedProps>().props;
  const underReview =
    (project.total_seconds ?? 0) - (project.approved_seconds ?? 0);
  const unshipped = project.reported_seconds - (project.total_seconds ?? 0);

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        <PageHeading
          eyebrow="Review"
          title={project.title ?? "Untitled project"}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="flex flex-col gap-6">
            <Panel title="submission.txt" tone="alt">
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge tone="info">{project.status}</Badge>
                  <span className="text-ink-muted flex items-center gap-1.5">
                    <Icon
                      src={clockIcon}
                      label="Time logged"
                      className="h-4 w-4"
                    />
                    <span className="font-display text-body font-bold">
                      {formatTime(project.reported_seconds)}
                    </span>
                  </span>
                  {project.high_quality && (
                    <Badge tone="brand">High quality</Badge>
                  )}
                </div>

                {/* Approving hours moves a real budget, so which one is stated
                    up front rather than left for the reviewer to go and find. */}
                <div className="border-line bg-surface-plain border-2 p-3">
                  {shipped_for ? (
                    <p className="text-body text-ink">
                      Shipped for <strong>{shipped_for.name}</strong> -
                      approving {formatTime(underReview)} adds{" "}
                      <strong>
                        $
                        {(
                          (underReview / 3600) *
                          (program.budget_per_hour_cents / 100)
                        ).toFixed(2)}
                      </strong>{" "}
                      to its budget.
                    </p>
                  ) : (
                    <p className="text-caption text-ink-muted">
                      No Boba Bash attached - a travel grant, which only HQ can
                      unlock. Approving it moves no event budget.
                    </p>
                  )}
                </div>

                <div className="text-caption text-ink-muted flex flex-col gap-1">
                  {project.real_approved_seconds > 0 && (
                    <p className="text-leaf font-bold">
                      {formatTime(project.real_approved_seconds)} approved
                      already
                    </p>
                  )}
                  {project.aasm_state === "submitted" && (
                    <p>
                      {formatTime(underReview)} under review
                      {unshipped > 0 &&
                        ` (${formatTime(unshipped)} logged since they shipped)`}
                    </p>
                  )}
                </div>

                {hackatime_projects.length > 0 && (
                  <div>
                    <p className="font-display text-caption text-ink font-bold">
                      Hackatime projects
                    </p>
                    <ul className="text-caption text-ink-muted mt-1 flex flex-col gap-0.5">
                      {hackatime_projects.map((hp) => (
                        <li key={hp.id}>
                          {hp.name}{" "}
                          <span className="text-ink-soft">
                            ({formatTime(hp.total_seconds)})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Panel>

            {project.screenshot && (
              <Panel title="screenshot.png" bodyClassName="p-3">
                <img
                  src={project.screenshot}
                  alt={`Screenshot of ${project.title}`}
                  className="border-line max-h-96 w-full border object-contain"
                />
              </Panel>
            )}

            {project.desc && (
              <Panel title="description.txt">
                <p className="text-body text-ink-muted wrap-break-word whitespace-pre-wrap">
                  {project.desc}
                </p>
              </Panel>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {project.demo_link && (
                <ButtonAnchor
                  href={project.demo_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open demo ↗
                </ButtonAnchor>
              )}
              {project.repo_link && (
                <ButtonAnchor
                  href={project.repo_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="secondary"
                >
                  Open repo ↗
                </ButtonAnchor>
              )}
              <ButtonAnchor
                href={`/projects/${project.id}/manage`}
                target="_blank"
                rel="noopener noreferrer"
                variant="ghost"
              >
                Project page ↗
              </ButtonAnchor>
            </div>

            {project.ai_declaration && (
              <Panel title="ai-declaration.txt" tone="alt">
                <p className="text-body text-ink-muted wrap-break-word whitespace-pre-wrap">
                  {project.ai_declaration}
                </p>
              </Panel>
            )}
          </div>

          <Panel title="author.txt" className="self-start">
            <div className="flex flex-col gap-3">
              <Avatar
                src={project.user!.avatar}
                name={project.user!.username}
                size="lg"
              />
              <div className="text-caption text-ink-muted flex min-w-0 flex-col gap-0.5">
                <a
                  href={`/admin/users/${project.user!.id}`}
                  className="font-display text-subheading text-link font-bold underline"
                >
                  {project.user!.username} ({project.user!.id})
                </a>
                <p className="wrap-anywhere">{project.user!.email}</p>
                <p>
                  <span className="text-ink font-bold">Hackatime:</span>{" "}
                  {project.user!.hackatime_id ?? "not linked"}
                  {project.user!.hackatime_id && (
                    <>
                      {" ("}
                      <a
                        href={`https://joe.fraud.hackclub.com/profile/${project.user!.hackatime_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-link underline"
                      >
                        Joe
                      </a>
                      {" | "}
                      <a
                        href={`https://billy.3kh0.net/?u=${project.user!.hackatime_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-link underline"
                      >
                        Billy
                      </a>
                      {")"}
                    </>
                  )}
                </p>
                <p>
                  <span className="text-ink font-bold">Slack:</span>{" "}
                  <a
                    href={`https://hackclub.enterprise.slack.com/team/${project.user!.slack_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-link underline"
                  >
                    {project.user!.slack_id ?? "not linked"}
                  </a>
                </p>
              </div>
            </div>
          </Panel>
        </div>

        <ProjectReviews
          project={project}
          ships={ships}
          attributableEvents={attributable_events}
        />
      </div>
    </Layout>
  );
}
